import { Injectable } from '@angular/core';
import { MenuItemDirective } from 'src/app/nav-menu/menu-item.directive';
import { Identity } from './identity.service';

@Injectable({
    providedIn: 'root'
})
export class MenuService {
    private loggedInUser: string = null;
    private modules: MenuItemDirective[] = [];
    static publicRoutes = ['/signin', '/signin/forgot-password', '/signin/reset-password'];
    static hiddenMenuRoutes = [
        'margin-calculator',
        'shift-management/notifications',
        'shift-management/shift',
        'nurse-availability/availability',
        'devops',
        'signin',
        'logout'
    ];

    private forEachSubItem(item: MenuItemDirective, callback: (subItem: MenuItemDirective) => void) {
        if (item.subItems.length) {
            for (const subItem of item.subItems) {
                callback(subItem);
            }
        }
    }

    private menuHasPermission(permissions: string[], menu: MenuItemDirective): boolean {
        return permissions.some((p) => p.startsWith(menu.name.toLowerCase() + ':'));
    }

    private menuItemHasPermission(
        permissions: string[],
        menu: MenuItemDirective,
        menuItem: MenuItemDirective
    ): boolean {
        const permissionName = `${menu.name.toLowerCase()}:${menuItem.name.toLowerCase()}`;
        //p.slice(0, -2) - remove last two characters because they are activation permissions $c/$r/etc.
        return permissions.some((p) => p.slice(0, -2) === permissionName);
    }

    isHiddenMenuRoute(url: string): boolean {
        //It's better to use startsWith, but this is a broader change.
        return MenuService.hiddenMenuRoutes.some((x) => url?.includes(x));
    }

    register(menuItem: MenuItemDirective) {
        this.modules.push(menuItem);

        menuItem.url = `/${menuItem.path}/${menuItem.defaultItem || 'dashboard'}`;

        this.forEachSubItem(menuItem, (subMenuItem) => {
            if (subMenuItem.path && menuItem.path.length) {
                if (menuItem.path && menuItem.path.length) {
                    subMenuItem.url = `/${menuItem.path}/${subMenuItem.path}`;
                } else {
                    subMenuItem.url = subMenuItem.path;
                }
            }

            this.forEachSubItem(subMenuItem, (subSubMenuItem) => {
                if (menuItem.path && menuItem.path.length) {
                    subSubMenuItem.url = `/${menuItem.path}/${subSubMenuItem.path}`;
                } else {
                    subSubMenuItem.url = subSubMenuItem.path;
                }
            });
        });
    }

    // Recompute visibility on every call (not just on username change).
    // Feature-flag-driven structure can change while identity remains the same.
    getVisibleItems(identity: Identity): MenuItemDirective[] {
        if (!this.modules.length || !identity) {
            return [];
        }

        const permissionsToken = identity.permissionsToken.toLowerCase();
        let permissions = permissionsToken.split(',');
        permissions = this.switchHomeMenuItem(permissions);

        for (const menu of this.modules) {
            menu.isVisible = this.menuHasPermission(permissions, menu);

            this.forEachSubItem(menu, (subItem) => {
                subItem.isVisible = false;
                if (this.menuItemHasPermission(permissions, menu, subItem)) {
                    menu.isVisible = true;
                    subItem.isVisible = true;
                }

                this.forEachSubItem(subItem, (subSubItem) => {
                    subSubItem.isVisible = false;
                    subSubItem.icon = subSubItem.icon || '_';

                    if (this.menuItemHasPermission(permissions, menu, subSubItem)) {
                        menu.isVisible = true;
                        subItem.isVisible = true;
                        subSubItem.isVisible = true;
                    }
                });
            });
        }

        return this.getModules();
    }

    switchHomeMenuItem(permissions: string[]) {
        if (permissions.some((p) => p.startsWith('recruiting:dashboard'))) {
            const perms = permissions.filter((p) => !p.startsWith('welcome:dashboard'));
            perms.push('_recruiting:candidates-search');
            return perms;
        }

        return permissions;
    }

    urlToModuleItem(url: string): { moduleName: string; itemName: string } {
        if (MenuService.publicRoutes.includes(url)) {
            return { moduleName: 'signin', itemName: null };
        }

        let result = { moduleName: null, itemName: null };

        if (url && url.length) {
            for (const menu of this.modules) {
                result = this.setMenuModules(result, menu, url);
            }
        }

        return result;
    }

    // Reset cached module registration so the nav can be rebuilt from current projected content.
    reset(): void {
        this.modules = [];
        this.loggedInUser = null;
    }

    private setMenuModules(result: { moduleName: string; itemName: string }, menu, url) {
        if (compareUrls(url, menu.url, menu.queryParams)) {
            result = { moduleName: menu.name, itemName: null };
        }

        this.forEachSubItem(menu, (menuItem) => {
            if (compareUrls(url, menuItem.url, menuItem.queryParams)) {
                result = { moduleName: menu.name, itemName: menuItem.path };
            }

            this.forEachSubItem(menuItem, (menuSubItem) => {
                if (compareUrls(url, menuSubItem.url, menuSubItem.queryParams)) {
                    result = { moduleName: menu.name, itemName: menuSubItem.path };
                }
            });
        });
        return result;
    }

    private getModules(): MenuItemDirective[] {
        return this.modules.filter(
            (item) =>
                (item.isVisible && item.subItems.length > 0 && item.subItems.some((x) => x.isVisible)) ||
                (item.isVisible && item.subItems.length === 0)
        );
    }
}

function compareUrls(url1: string, url2: string, queryParams: any): boolean {
    const queryString = queryParams
        ? Object.entries(queryParams)
              .map((kvp) => `${kvp[0]}=${kvp[1]}`)
              .join('&')
        : null;
    return (
        url1 &&
        url2 &&
        url1.toLowerCase().startsWith(url2.toLowerCase()) &&
        (!queryString || url1.includes(queryString))
    );
}
